import {
	getCoreRowModel,
	getFilteredRowModel,
	Row,
	Table,
	useReactTable,
	ColumnDef,
} from "@tanstack/react-table"
import { memo, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "../../../context/AuthContext"
import useBreakpoint, { ScreenBreakpoint } from "../../../hooks/useBreakpoint"
import { DataTable } from "../../ui/data-table/data-table"
import { getColumns } from "./columns"
import { getMovementsColumns } from "./columns/movementsColumns"
import { useTableData } from "../../../context/TableDataContext"
import {
	TableData,
	TableMovement,
	TableOrder,
} from "./data/types"
import { getCustomersColumns } from "./columns/customersColumns"

export const ROW_HEIGHT = 70
export const rowHeightClassName = "h-[70px]"

// Memoized table components
interface TableViewProps<T> {
	isLoading: boolean
	viewOptions: boolean
	bordered: boolean
	items: T[]
	visibleRowCount: number
	columns: ColumnDef<T, any>[]
	columnVisibility: Record<string, boolean>
}

// Define TableView as a generic functional component without memo
function TableView<T extends TableData>({
	isLoading,
	viewOptions,
	bordered,
	items,
	visibleRowCount,
	columns,
	columnVisibility,
}: TableViewProps<T>) {
	return (
		<DataTable<T, any>
			loading={isLoading}
			viewOptions={viewOptions}
			rowHeightClass={rowHeightClassName}
			columns={columns}
			data={items.slice(0, visibleRowCount)}
			variant={bordered ? "bordered" : "default"}
			columnVisibility={columnVisibility}
			modalConfig={{
				enableModal: true,
			}}
		/>
	)
}

// Create typed components for each data type and wrap them with memo
const TableOrderView = memo(
	(props: TableViewProps<TableOrder>) => <TableView<TableOrder> {...props} />,
	(prevProps, nextProps) =>
		prevProps.items === nextProps.items &&
		prevProps.visibleRowCount === nextProps.visibleRowCount &&
		prevProps.columnVisibility === nextProps.columnVisibility
)

const TableMovementView = memo(
	(props: TableViewProps<TableMovement>) => (
		<TableView<TableMovement> {...props} />
	),
	(prevProps, nextProps) =>
		prevProps.items === nextProps.items &&
		prevProps.visibleRowCount === nextProps.visibleRowCount &&
		prevProps.columnVisibility === nextProps.columnVisibility
)

const TableCustomerView = memo(
	(props: TableViewProps<any>) => <TableView<any> {...props} />,
	(prevProps, nextProps) =>
		prevProps.items === nextProps.items &&
		prevProps.visibleRowCount === nextProps.visibleRowCount &&
		prevProps.columnVisibility === nextProps.columnVisibility
)

interface TxTableProps {
	scrollable?: boolean
	viewOptions?: boolean
	bordered?: boolean
}

const TxTable = memo(
	({
		scrollable = false,
		viewOptions = false,
		bordered = false,
	}: TxTableProps) => {
		const tableContainerRef = useRef<HTMLDivElement>(null)
		const [visibleRowCount, setVisibleRowCount] = useState(0)
		const { stores } = useAuth()
		const {
			tableRef,
			payments,
			movements,
			filteredPayments,
			columnFilters,
			setColumnFilters,
			setColumnVisibility,
			columnVisibility,
			currentView,
			processingProgress,
			movementsProcessingProgress,
			customersData,
			customersLoading,
		} = useTableData()

		const breakpoint = useBreakpoint()

		const ordersTableOptions = useMemo(
			() => ({
				data: filteredPayments,
				columns: getColumns(stores)[breakpoint as ScreenBreakpoint],
				getCoreRowModel: getCoreRowModel(),
				getFilteredRowModel: getFilteredRowModel(),
				state: {
					columnFilters,
					columnVisibility,
				},
				onColumnFiltersChange: setColumnFilters,
				onColumnVisibilityChange: setColumnVisibility,
				enableFilters: true,
				manualFiltering: false,
			}),
			[filteredPayments, stores, breakpoint, columnFilters, columnVisibility]
		)

		const movementsTableOptions = useMemo(
			() => ({
				data: movements,
				columns: getMovementsColumns(stores)[breakpoint as ScreenBreakpoint],
				getCoreRowModel: getCoreRowModel(),
				getFilteredRowModel: getFilteredRowModel(),
				state: {
					columnFilters,
					columnVisibility,
				},
				onColumnFiltersChange: setColumnFilters,
				onColumnVisibilityChange: setColumnVisibility,
				globalFilterFn: (
					row: Row<TableMovement>,
					columnId: string,
					filterValue: string[]
				) => {
					const storeId = String(row.getValue("store_name"))
					// Invert the selection: exclude stores in filterValue
					return !filterValue.includes(storeId)
				},
			}),
			[movements, stores, breakpoint, columnFilters, columnVisibility]
		)

		const customersTableOptions = useMemo(
			() => ({
				data: customersData,
				columns: getCustomersColumns(breakpoint as ScreenBreakpoint),
				getCoreRowModel: getCoreRowModel(),
				getFilteredRowModel: getFilteredRowModel(),
				state: {
					columnFilters,
					columnVisibility,
				},
				onColumnFiltersChange: setColumnFilters,
				onColumnVisibilityChange: setColumnVisibility,
				enableFilters: true,
				manualFiltering: false,
			}),
			[customersData, breakpoint, columnFilters, columnVisibility]
		)

		const ordersTable = useReactTable(ordersTableOptions)

		const movementsTable = useReactTable(movementsTableOptions)

		const customersTable = useReactTable(customersTableOptions)

		const filteredOrders = useMemo(() => {
			if (currentView !== "orders") return []
			return (
				ordersTable?.getFilteredRowModel().rows.map((row) => row.original) ?? []
			)
		}, [currentView, columnFilters, filteredPayments])

		const filteredMovements = useMemo(() => {
			if (currentView !== "movements") return []
			return (
				movementsTable?.getFilteredRowModel().rows.map((row) => row.original) ??
				[]
			)
		}, [currentView, columnFilters, movements])

		const filteredCustomers = useMemo(() => {
			if (currentView !== "customers") return []
			return (
				customersTable?.getFilteredRowModel().rows.map((row) => row.original) ??
				[]
			)
		}, [currentView, columnFilters, customersData])

		// Effect for calculating visible rows
		useEffect(() => {
			const calculateVisibleRows = () => {
				if (!scrollable && tableContainerRef.current) {
					const containerHeight = tableContainerRef.current.clientHeight
					const calculatedRows = Math.floor(containerHeight / ROW_HEIGHT)
					setVisibleRowCount(Math.max(1, calculatedRows - 1))
				} else if (scrollable) {
					if (
						payments?.length > 0 ||
						customersData?.length > 0 ||
						movements?.length > 0
					) {
						setVisibleRowCount(
							Math.max(
								payments?.length,
								customersData?.length,
								movements?.length
							)
						)
					} else {
						setVisibleRowCount(0)
					}
				}
			}

			calculateVisibleRows()
			window.addEventListener("resize", calculateVisibleRows)
			return () => window.removeEventListener("resize", calculateVisibleRows)
		}, [payments, scrollable, customersData, movements])

		useEffect(() => {
			switch (currentView) {
				case "orders":
					tableRef.current = ordersTable as Table<TableData>
					break
				case "movements":
					tableRef.current = movementsTable as Table<TableData>
					break
				case "customers":
					tableRef.current = customersTable as Table<TableData>
					break
			}
		}, [ordersTable, movementsTable, customersTable, currentView])

		return (
			<div
				className="h-full flex flex-col [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
				ref={tableContainerRef}
			>
				{currentView === "orders" && (
					<TableOrderView
						key={`orders-${JSON.stringify(columnFilters)}`}
						isLoading={processingProgress < 1}
						viewOptions={viewOptions}
						bordered={bordered}
						items={filteredOrders}
						visibleRowCount={visibleRowCount}
						columns={getColumns(stores)[breakpoint as ScreenBreakpoint]}
						columnVisibility={columnVisibility}
					/>
				)}
				{currentView === "movements" && (
					<TableMovementView
						key={`movements-${JSON.stringify(columnFilters)}`}
						isLoading={movementsProcessingProgress}
						viewOptions={viewOptions}
						bordered={bordered}
						items={filteredMovements}
						visibleRowCount={visibleRowCount}
						columns={
							getMovementsColumns(stores)[breakpoint as ScreenBreakpoint]
						}
						columnVisibility={columnVisibility}
					/>
				)}
				{currentView === "customers" && (
					<TableCustomerView
						key={`customers-${JSON.stringify(columnFilters)}`}
						isLoading={customersLoading}
						viewOptions={viewOptions}
						bordered={bordered}
						items={filteredCustomers}
						visibleRowCount={visibleRowCount}
						columns={getCustomersColumns(breakpoint as ScreenBreakpoint)}
						columnVisibility={columnVisibility}
					/>
				)}
			</div>
		)
	},
	(prevProps: TxTableProps, nextProps: TxTableProps) =>
		prevProps.scrollable === nextProps.scrollable &&
		prevProps.viewOptions === nextProps.viewOptions &&
		prevProps.bordered === nextProps.bordered
)

export default TxTable
